import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { observer } from 'mobx-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Maybe } from 'maybeasy';
import { Link } from '../../../../../../Resource/Types';
import { TranslationsContext, translation } from '../../../../../../Translations';
import { UploadImageStore } from '../../../Edit/CurrentCollectionStep/Details/UploadImageStore';
import LogoUploader from './LogoUploader';
import RemoveLogoModal from './RemoveLogoModal';
import { emptyFragment } from '../../../../../../EmptyFragment';
import { EditLearningCollectionResource, LearningCollectionResource } from '../Types';
import { useLearningCollection } from '../LearningCollectionContext';

type ResourceType = LearningCollectionResource | EditLearningCollectionResource;

interface Props {
  logoLink: Maybe<Link>;
  uploadLink: Maybe<Link>;
  links: ReadonlyArray<Link>;
  resourceState: ResourceType;
  setResourceState: (resource: ResourceType | ((prev: ResourceType) => ResourceType)) => void;
}

function OrganizationLogo({ logoLink, uploadLink, links, resourceState, setResourceState }: Props) {
  const ts = useContext(TranslationsContext);
  const { setLogoChange } = useLearningCollection();
  const [isHovered, setIsHovered] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [uploadImageStore] = useState(() => new UploadImageStore());
  const containerRef = useRef<HTMLDivElement>(null);

  const handleEditClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      uploadImageStore.reset();
      setIsEditing(true);
    },
    [uploadImageStore],
  );

  // DVLP-7971: removing (after the confirmation) drops the link from the
  // resource, so the control falls back to the uploader, and records the
  // change for the next save. An upload after that replaces the removal.
  const handleRemove = useCallback(() => {
    uploadImageStore.reset();
    setIsHovered(false);
    setLogoChange({ kind: 'remove' });
    setResourceState((prev: ResourceType) => ({
      ...prev,
      links: prev.links.filter(
        (link: Link) => link.rel !== 'learning-collection-logo',
      ) as typeof prev.links,
    }));
  }, [uploadImageStore, setLogoChange, setResourceState]);

  // Handle click outside to exit edit mode
  useEffect(() => {
    if (!isEditing) return;

    const handleClickOutside = (event: MouseEvent) => {
      // Don't allow exit while upload is in progress
      if (uploadImageStore.state.kind === 'loading') {
        return;
      }

      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isEditing, uploadImageStore.state.kind]);

  // Watch for successful upload and exit edit mode
  // This effect reacts to MobX observable state transitions, which is the standard
  // pattern for MobX+React integration. The store doesn't support callbacks.
  /* eslint-disable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates -- MobX observable reaction pattern; store doesn't support completion callbacks */
  useEffect(() => {
    if (!isEditing) return;

    // When state transitions to 'ready' with file metadata, upload succeeded
    if (uploadImageStore.state.kind === 'ready' && uploadImageStore.state.fileMetaData.isJust()) {
      setIsEditing(false);
    }
  }, [isEditing, uploadImageStore.state]);
  /* eslint-enable react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-chain-state-updates */

  const hasLogo = logoLink.isJust();

  // STATE: No logo (empty) - show uploader directly, no pencil
  if (!hasLogo && !isEditing) {
    return uploadLink
      .map((link) => (
        <LogoUploader
          uploadImageStore={uploadImageStore}
          uploadLink={link}
          links={links}
          resourceState={resourceState}
          setResourceState={setResourceState}
        />
      ))
      .getOrElse(emptyFragment);
  }

  // STATE: Editing (has logo but user clicked pencil) - show uploader
  if (isEditing) {
    return uploadLink
      .map((link) => (
        <div ref={containerRef}>
          <LogoUploader
            uploadImageStore={uploadImageStore}
            uploadLink={link}
            links={links}
            resourceState={resourceState}
            setResourceState={setResourceState}
          />
        </div>
      ))
      .getOrElse(emptyFragment);
  }

  // STATE: View (has logo, not editing) - the logo, with pencil and trash on
  // hover. The icons stay mounted and fade in with CSS so the trash keeps its
  // anchored confirmation open while the pointer is over the dialog.
  return (
    <div
      ref={containerRef}
      className="group relative md:basis-1/4 md:shrink-0 md:grow-0"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="absolute top-2 right-2 z-10 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <button
          onClick={handleEditClick}
          className="rounded-md p-1 transition-colors text-muted-foreground hover:text-foreground"
          aria-label={translation('Edit logo')(ts)}
          data-intercom-target="builder-v2-logo-edit-button"
        >
          <PencilIcon className="h-4 w-4" />
        </button>
        <RemoveLogoModal
          logoHref={logoLink.map(({ href }) => href).getOrElseValue('')}
          onConfirm={handleRemove}
        >
          {({ onOpen }) => (
            <button
              type="button"
              onClick={onOpen}
              className="rounded-md p-1 transition-colors text-muted-foreground hover:text-destructive"
              aria-label={translation('Remove logo')(ts)}
              data-intercom-target="builder-v2-logo-remove-button"
            >
              <TrashIcon className="h-4 w-4" />
            </button>
          )}
        </RemoveLogoModal>
      </div>
      {logoLink
        .map(({ href, rel }: Link) => (
          <div
            className="w-full overflow-hidden flex justify-end cursor-pointer"
            onClick={isHovered ? handleEditClick : undefined}
          >
            <img
              src={href}
              alt={rel}
              className="h-auto max-h-48 max-w-full object-contain rounded-md"
            />
          </div>
        ))
        .getOrElse(emptyFragment)}
    </div>
  );
}

export default observer(OrganizationLogo);
